import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

// Flag SVGs are imported per country as plain strings (a few hundred bytes
// each), so only these five ship in the bundle.
import CN from 'country-flag-icons/string/3x2/CN'
import DE from 'country-flag-icons/string/3x2/DE'
import ES from 'country-flag-icons/string/3x2/ES'
import FR from 'country-flag-icons/string/3x2/FR'
import GB from 'country-flag-icons/string/3x2/GB'
import IT from 'country-flag-icons/string/3x2/IT'
import NL from 'country-flag-icons/string/3x2/NL'

import { storeLanguage, type Language } from '../i18n'

const LANGUAGES: { code: Language; label: string; flag: string }[] = [
  { code: 'en', label: 'English', flag: GB },
  { code: 'nl', label: 'Nederlands', flag: NL },
  { code: 'fr', label: 'Français', flag: FR },
  { code: 'de', label: 'Deutsch', flag: DE },
  { code: 'it', label: 'Italiano', flag: IT },
  { code: 'zh', label: '中文', flag: CN },
  { code: 'es', label: 'Español', flag: ES },
]

function LanguageSwitcher({ variant }: { variant: 'navbar' | 'footer' }) {
  const { t, i18n } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()

  function choose(code: Language) {
    if (code === i18n.language) return
    const segments = location.pathname.split('/')
    segments[1] = code // swap the language prefix, keep the rest of the path
    storeLanguage(code)
    navigate(segments.join('/') + location.search + location.hash)
  }

  return (
    <div
      className={`lang-switcher lang-switcher-${variant}`}
      role="group"
      aria-label={t('nav.language')}
    >
      {LANGUAGES.map(({ code, label, flag }) => (
        <button
          key={code}
          type="button"
          className={`lang-option${i18n.language === code ? ' is-active' : ''}`}
          aria-current={i18n.language === code ? 'true' : undefined}
          // WCAG 2.5.3 (Label in Name): the accessible name must contain the
          // visible text. The footer variant already shows the full endonym
          // (aria-label === visible text, no change needed); the navbar
          // variant shows only the 2-letter code, so "Nederlands" alone as
          // the label used to fail that against "NL" on screen - prefixing
          // the code onto the name fixes it for navbar without changing
          // what the footer already had right.
          aria-label={variant === 'navbar' ? `${code.toUpperCase()} - ${label}` : label}
          onClick={() => choose(code)}
        >
          <span
            className="lang-flag"
            aria-hidden="true"
            dangerouslySetInnerHTML={{ __html: flag }}
          />
          {/* Footer shows the full endonym; the compact utility-bar variant
              shows the 2-letter code instead (CSS switches which is shown). */}
          <span className="lang-option-label">{label}</span>
          <span className="lang-option-code" aria-hidden="true">
            {code}
          </span>
        </button>
      ))}
    </div>
  )
}

export default LanguageSwitcher
